import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DirhamIconColored } from '../components/DirhamIcon';
import CurrencyDisplay from '../components/CurrencyDisplay';
import useElementToPdf from '../utils/useElementToPdf';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Banknote,
  CreditCard,
  Calculator,
  Calendar,
  Download,
  Printer,
  ChevronDown,
  Building2,
  User,
  FileDown,
  FileBarChart,
  Settings,
  Mail,
  Clock,
  Bell,
  Check,
  X,
  Languages,
  Globe
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';

const Reports = () => {
  const { t } = useTranslation();
  const { isAdmin, isBarber } = useAuth();
  const { language } = useLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBarber, setSelectedBarber] = useState('');
  const [barbers, setBarbers] = useState([]);
  const printRef = useRef();
  
  // PDF Language settings
  const [pdfLanguage, setPdfLanguage] = useState(language); // Use interface language by default
  const [bilingualPdf, setBilingualPdf] = useState(false);
  
  // Schedule settings state
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleSettings, setScheduleSettings] = useState({
    enabled: false,
    day_of_month: 1,
    hour: 9,
    minute: 0,
    period: 'monthly',
    recipients: [],
    include_charts: true,
    send_whatsapp: false
  });
  const [newRecipient, setNewRecipient] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (isAdmin()) {
      fetchBarbers();
      fetchScheduleSettings();
    }
    fetchReport();
  }, [period, selectedDate, selectedBarber]);
  
  const fetchScheduleSettings = async () => {
    try {
      const res = await api.get('/reports/schedule');
      setScheduleSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch schedule settings');
    }
  };
  
  const saveScheduleSettings = async () => {
    setSavingSchedule(true);
    try {
      await api.put('/reports/schedule', scheduleSettings);
      toast.success('تم حفظ إعدادات الجدولة بنجاح');
      setShowScheduleDialog(false);
      fetchScheduleSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حفظ الإعدادات');
    } finally {
      setSavingSchedule(false);
    }
  };
  
  const sendTestReport = async () => {
    setSendingTest(true);
    try {
      const res = await api.post('/reports/send-scheduled-report');
      if (res.data.sent) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال التقرير');
    } finally {
      setSendingTest(false);
    }
  };
  
  const addRecipient = () => {
    if (newRecipient && newRecipient.includes('@')) {
      setScheduleSettings(prev => ({
        ...prev,
        recipients: [...prev.recipients, newRecipient]
      }));
      setNewRecipient('');
    }
  };
  
  const removeRecipient = (email) => {
    setScheduleSettings(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  const fetchBarbers = async () => {
    try {
      const res = await api.get('/barbers');
      setBarbers(res.data);
    } catch (error) {
      console.error('Failed to fetch barbers');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `/reports/detailed?period=${period}&date=${selectedDate}`;
      if (selectedBarber) {
        url += `&barber_id=${selectedBarber}`;
      }
      const res = await api.get(url);
      setReport(res.data);
    } catch (error) {
      toast.error('فشل تحميل التقرير');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: `تقرير_${period}_${selectedDate}`,
    pageStyle: `
      @page { size: A4; margin: 15mm; }
      @media print {
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `
  });

  // Download PDF Reports
  const downloadPDF = async (type, reportPeriod = 'monthly') => {
    try {
      toast.info('جاري إنشاء التقرير...');
      const langParam = `&lang=${pdfLanguage}&bilingual=${bilingualPdf}`;
      const response = await api.get(`/reports/advanced/${type}?period=${reportPeriod}${langParam}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${reportPeriod}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل التقرير بنجاح');
    } catch (error) {
      toast.error('فشل تحميل التقرير');
    }
  };

  // Export All Reports (Admin only)
  const [exportingAll, setExportingAll] = useState(false);
  const exportAllReports = async () => {
    try {
      setExportingAll(true);
      toast.info('جاري إنشاء تقرير شامل لجميع الأقسام...');
      const langParam = `&lang=${pdfLanguage}&bilingual=${bilingualPdf}`;
      const response = await api.post(`/reports/export-all?period=${period}${langParam}`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_reports_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تصدير جميع التقارير بنجاح!');
    } catch (error) {
      toast.error('فشل تصدير التقارير');
    } finally {
      setExportingAll(false);
    }
  };

  if (!isAdmin() && !isBarber()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-3xl font-bold">{t('reports')}</h1>
          <p className="text-muted-foreground">تقارير مفصلة للإيرادات والعملاء</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Period Selector */}
          <div className="flex bg-secondary/50 rounded-lg p-1">
            <Button
              variant={period === 'daily' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPeriod('daily')}
              className={period === 'daily' ? 'bg-primary text-primary-foreground' : ''}
            >
              يومي
            </Button>
            <Button
              variant={period === 'weekly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPeriod('weekly')}
              className={period === 'weekly' ? 'bg-primary text-primary-foreground' : ''}
            >
              أسبوعي
            </Button>
            <Button
              variant={period === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPeriod('monthly')}
              className={period === 'monthly' ? 'bg-primary text-primary-foreground' : ''}
            >
              شهري
            </Button>
          </div>

          {/* Date Selector */}
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-secondary/50 border-white/10 w-40"
            />
          </div>

          {/* Barber Filter (Admin only) */}
          {isAdmin() && (
            <select
              value={selectedBarber}
              onChange={(e) => setSelectedBarber(e.target.value)}
              className="bg-secondary/50 border border-white/10 rounded-lg px-4 py-2"
            >
              <option value="">كل الحلاقين</option>
              {barbers.map(barber => (
                <option key={barber.id} value={barber.id}>
                  {barber.name}
                </option>
              ))}
            </select>
          )}

          {/* Print Button */}
          <Button onClick={handlePrint} variant="outline" className="border-white/10">
            <Printer className="w-4 h-4 me-2" />
            تنزيل PDF
          </Button>

          {/* PDF Download Dropdown */}
          {isAdmin() && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-green-500/50 text-green-500 hover:bg-green-500/10">
                  <FileBarChart className="w-4 h-4 me-2" />
                  تقارير PDF
                  <ChevronDown className="w-4 h-4 ms-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-white/10 w-56">
                {/* Language Settings */}
                <div className="p-2 border-b border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      لغة التقرير
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setPdfLanguage('ar'); }}
                        className={`px-2 py-0.5 text-xs rounded ${pdfLanguage === 'ar' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
                      >
                        عربي
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setPdfLanguage('en'); }}
                        className={`px-2 py-0.5 text-xs rounded ${pdfLanguage === 'en' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
                      >
                        EN
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Languages className="w-3 h-3" />
                      ثنائي اللغة
                    </span>
                    <Switch
                      checked={bilingualPdf}
                      onCheckedChange={setBilingualPdf}
                      className="scale-75"
                    />
                  </div>
                </div>
                
                <DropdownMenuItem onClick={() => downloadPDF('revenues', period)} className="cursor-pointer">
                  <TrendingUp className="w-4 h-4 me-2 text-green-500" />
                  تقرير الإيرادات
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadPDF('expenses', period)} className="cursor-pointer">
                  <CreditCard className="w-4 h-4 me-2 text-red-500" />
                  تقرير المصروفات
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadPDF('invoices', period)} className="cursor-pointer">
                  <Banknote className="w-4 h-4 me-2 text-blue-500" />
                  تقرير الفواتير
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadPDF('housing', period)} className="cursor-pointer">
                  <Building2 className="w-4 h-4 me-2 text-purple-500" />
                  تقرير السكن
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadPDF('full-summary', period)} className="cursor-pointer">
                  <FileDown className="w-4 h-4 me-2 text-primary" />
                  تقرير شامل
                </DropdownMenuItem>
                <div className="border-t border-white/10 my-1"></div>
                <DropdownMenuItem 
                  onClick={exportAllReports} 
                  className="cursor-pointer bg-gradient-to-r from-[#d4af37]/20 to-transparent"
                  disabled={exportingAll}
                >
                  <FileBarChart className="w-4 h-4 me-2 text-[#d4af37]" />
                  {exportingAll ? 'جاري التصدير...' : '📦 تصدير جميع التقارير'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Schedule Settings Button (Admin only) */}
          {isAdmin() && (
            <Button 
              onClick={() => setShowScheduleDialog(true)} 
              variant="outline" 
              className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
              data-testid="schedule-reports-btn"
            >
              <Bell className="w-4 h-4 me-2" />
              جدولة التقارير
            </Button>
          )}
        </div>
      </div>
      
      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="bg-card border-white/10 max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Bell className="w-5 h-5 text-primary" />
              جدولة إرسال التقارير التلقائي
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div>
                <p className="font-medium">تفعيل الإرسال التلقائي</p>
                <p className="text-sm text-muted-foreground">إرسال تقرير شامل بالبريد الإلكتروني</p>
              </div>
              <Switch
                checked={scheduleSettings.enabled}
                onCheckedChange={(checked) => setScheduleSettings(prev => ({...prev, enabled: checked}))}
              />
            </div>
            
            {/* Schedule Settings */}
            <div className={`space-y-4 ${!scheduleSettings.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
              {/* Period Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">نوع التقرير</label>
                <div className="flex gap-2">
                  {[
                    { value: 'weekly', label: 'أسبوعي' },
                    { value: 'monthly', label: 'شهري' },
                    { value: 'bimonthly', label: 'شهرين' }
                  ].map(opt => (
                    <Button
                      key={opt.value}
                      size="sm"
                      variant={scheduleSettings.period === opt.value ? 'default' : 'outline'}
                      onClick={() => setScheduleSettings(prev => ({...prev, period: opt.value}))}
                      className={scheduleSettings.period === opt.value ? 'bg-primary' : ''}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Day of Month */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">يوم الإرسال (1-28)</label>
                  <Input
                    type="number"
                    min={1}
                    max={28}
                    value={scheduleSettings.day_of_month}
                    onChange={(e) => setScheduleSettings(prev => ({...prev, day_of_month: parseInt(e.target.value) || 1}))}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">الساعة (0-23)</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={scheduleSettings.hour}
                      onChange={(e) => setScheduleSettings(prev => ({...prev, hour: parseInt(e.target.value) || 0}))}
                      className="bg-secondary/50 border-white/10 w-20"
                      placeholder="ساعة"
                    />
                    <span className="text-2xl">:</span>
                    <Input
                      type="number"
                      min={0}
                      max={59}
                      value={scheduleSettings.minute}
                      onChange={(e) => setScheduleSettings(prev => ({...prev, minute: parseInt(e.target.value) || 0}))}
                      className="bg-secondary/50 border-white/10 w-20"
                      placeholder="دقيقة"
                    />
                  </div>
                </div>
              </div>
              
              {/* Recipients */}
              <div>
                <label className="text-sm font-medium mb-2 block">البريد الإلكتروني للمستلمين</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    type="email"
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    placeholder="example@email.com"
                    className="bg-secondary/50 border-white/10"
                    onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                  />
                  <Button onClick={addRecipient} size="sm" variant="outline">
                    إضافة
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {scheduleSettings.recipients.map((email) => (
                    <div key={email} className="flex items-center gap-1 bg-primary/20 text-primary px-2 py-1 rounded-full text-sm">
                      <Mail className="w-3 h-3" />
                      {email}
                      <button onClick={() => removeRecipient(email)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {scheduleSettings.recipients.length === 0 && (
                    <p className="text-sm text-muted-foreground">لم تتم إضافة أي بريد إلكتروني</p>
                  )}
                </div>
              </div>
              
              {/* Status */}
              {scheduleSettings.last_sent && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-sm text-green-500">
                    <Check className="w-4 h-4 inline me-1" />
                    آخر إرسال: {new Date(scheduleSettings.last_sent).toLocaleString('ar-SA')}
                  </p>
                </div>
              )}
              
              {scheduleSettings.next_run && scheduleSettings.enabled && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-sm text-blue-500">
                    <Clock className="w-4 h-4 inline me-1" />
                    الإرسال القادم: {new Date(scheduleSettings.next_run).toLocaleString('ar-SA')}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              onClick={sendTestReport}
              variant="outline"
              disabled={sendingTest || scheduleSettings.recipients.length === 0}
              className="border-orange-500/50 text-orange-500"
            >
              {sendingTest ? 'جاري الإرسال...' : '🧪 إرسال تجريبي'}
            </Button>
            <Button
              onClick={saveScheduleSettings}
              disabled={savingSchedule}
              className="bg-primary text-primary-foreground"
            >
              {savingSchedule ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-2xl font-bold">KE Gents Saloon</h1>
        <p className="text-lg">{report?.period_label}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : report && (
        <>
          {/* Period Label */}
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-primary">{report.period_label}</p>
          </div>

          {/* Salon Summary */}
          <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-bold">ملخص الصالون الكامل</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {/* Total Revenue */}
              <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl p-4 border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">إجمالي الإيرادات</span>
                </div>
                <p className="text-2xl font-bold text-primary">
                  <CurrencyDisplay amount={report.salon_summary.total_revenue} className="text-primary" useLocale={true} />
                </p>
              </div>

              {/* Total Clients */}
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-xl p-4 border border-blue-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-sm text-muted-foreground">إجمالي العملاء</span>
                </div>
                <p className="text-2xl font-bold text-blue-500">
                  {report.salon_summary.total_clients}
                </p>
              </div>

              {/* Cash */}
              <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-xl p-4 border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Banknote className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-muted-foreground">كاش</span>
                </div>
                <p className="text-xl font-bold text-green-500">
                  <CurrencyDisplay amount={report.salon_summary.cash_total} className="text-green-500" useLocale={true} />
                </p>
                <p className="text-sm text-green-400">
                  {report.salon_summary.cash_clients} عميل
                </p>
              </div>

              {/* Machine */}
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-xl p-4 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-purple-500" />
                  <span className="text-sm text-muted-foreground">مكينة</span>
                </div>
                <p className="text-xl font-bold text-purple-500">
                  <CurrencyDisplay amount={report.salon_summary.machine_total} className="text-purple-500" useLocale={true} />
                </p>
                <p className="text-sm text-purple-400">
                  {report.salon_summary.machine_clients} عميل
                </p>
              </div>

              {/* Average per Client */}
              <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 rounded-xl p-4 border border-yellow-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm text-muted-foreground">متوسط العميل</span>
                </div>
                <p className="text-2xl font-bold text-yellow-500">
                  <CurrencyDisplay amount={report.salon_summary.avg_per_client} className="text-yellow-500" useLocale={true} />
                </p>
              </div>
            </div>

            {/* Profit Summary */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-secondary/30 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">إجمالي المصروفات</p>
                <p className="text-xl font-bold text-red-500">
                  <CurrencyDisplay amount={report.salon_summary.total_expenses} className="text-red-500" useLocale={true} />
                </p>
              </div>
              <div className="bg-secondary/30 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">صافي الربح</p>
                <p className={`text-xl font-bold ${report.salon_summary.net_profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  <CurrencyDisplay amount={report.salon_summary.net_profit} className={report.salon_summary.net_profit >= 0 ? 'text-green-500' : 'text-red-500'} useLocale={true} />
                </p>
              </div>
              <div className="bg-secondary/30 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">عدد العمليات</p>
                <p className="text-xl font-bold text-primary">
                  {report.salon_summary.transactions_count}
                </p>
              </div>
            </div>

            {/* Calculation Formula */}
            <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/10">
              <p className="text-sm text-center text-muted-foreground">
                <span className="font-bold text-primary">معادلة متوسط العميل:</span>{' '}
                إجمالي الإيرادات ({report.salon_summary.total_revenue.toLocaleString()}) ÷ 
                عدد العملاء ({report.salon_summary.total_clients}) = 
                <span className="font-bold text-yellow-500"> <CurrencyDisplay amount={report.salon_summary.avg_per_client} className="text-yellow-500" useLocale={true} /></span>
              </p>
            </div>
          </div>

          {/* Barbers Breakdown */}
          {report.barbers_breakdown && report.barbers_breakdown.length > 0 && (
            <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold">تفصيل كل حلاق</h3>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50 border-b border-white/5">
                    <tr>
                      <th className="px-4 py-3 text-start text-sm font-semibold">الحلاق</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">الإيرادات</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">العملاء</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">كاش (عملاء)</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">مكينة (عملاء)</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">متوسط العميل</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">العمليات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.barbers_breakdown.map((barber, index) => (
                      <tr key={barber.barber_id} className={`border-b border-white/5 hover:bg-secondary/20 ${index === 0 ? 'bg-primary/5' : ''}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {index === 0 && <span className="text-yellow-500">🏆</span>}
                            <span className="font-medium">{barber.barber_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 font-bold text-primary">
                          <CurrencyDisplay amount={barber.total_revenue} className="text-primary" useLocale={true} />
                        </td>
                        <td className="px-4 py-4">
                          <span className="bg-blue-500/20 text-blue-500 px-2 py-1 rounded-full text-sm">
                            {barber.total_clients}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="text-green-500 font-semibold"><CurrencyDisplay amount={barber.cash_total} className="text-green-500" useLocale={true} /></span>
                            <span className="text-xs text-green-400">({barber.cash_clients} عميل)</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="text-purple-500 font-semibold"><CurrencyDisplay amount={barber.machine_total} className="text-purple-500" useLocale={true} /></span>
                            <span className="text-xs text-purple-400">({barber.machine_clients} عميل)</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-yellow-500 font-bold"><CurrencyDisplay amount={barber.avg_per_client} className="text-yellow-500" useLocale={true} /></span>
                        </td>
                        <td className="px-4 py-4 text-muted-foreground">
                          {barber.transactions_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Total Row */}
                  <tfoot className="bg-primary/10 font-bold">
                    <tr>
                      <td className="px-4 py-4">المجموع</td>
                      <td className="px-4 py-4 text-primary"><CurrencyDisplay amount={report.salon_summary.total_revenue} className="text-primary" useLocale={true} /></td>
                      <td className="px-4 py-4 text-blue-500">{report.salon_summary.total_clients}</td>
                      <td className="px-4 py-4 text-green-500">
                        <CurrencyDisplay amount={report.salon_summary.cash_total} className="text-green-500" useLocale={true} />
                        <span className="text-xs block">({report.salon_summary.cash_clients} عميل)</span>
                      </td>
                      <td className="px-4 py-4 text-purple-500">
                        <CurrencyDisplay amount={report.salon_summary.machine_total} className="text-purple-500" useLocale={true} />
                        <span className="text-xs block">({report.salon_summary.machine_clients} عميل)</span>
                      </td>
                      <td className="px-4 py-4 text-yellow-500"><CurrencyDisplay amount={report.salon_summary.avg_per_client} className="text-yellow-500" useLocale={true} /></td>
                      <td className="px-4 py-4">{report.salon_summary.transactions_count}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Visual Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cash vs Machine Pie Chart Alternative */}
            <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
              <h3 className="text-lg font-semibold mb-4">توزيع طرق الدفع</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="flex items-center gap-2">
                      <Banknote className="w-4 h-4 text-green-500" />
                      كاش
                    </span>
                    <span className="text-green-500 font-bold">
                      {report.salon_summary.total_revenue > 0 
                        ? Math.round((report.salon_summary.cash_total / report.salon_summary.total_revenue) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="h-4 bg-secondary/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500"
                      style={{ 
                        width: report.salon_summary.total_revenue > 0 
                          ? `${(report.salon_summary.cash_total / report.salon_summary.total_revenue) * 100}%`
                          : '0%'
                      }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-purple-500" />
                      مكينة
                    </span>
                    <span className="text-purple-500 font-bold">
                      {report.salon_summary.total_revenue > 0 
                        ? Math.round((report.salon_summary.machine_total / report.salon_summary.total_revenue) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="h-4 bg-secondary/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-500"
                      style={{ 
                        width: report.salon_summary.total_revenue > 0 
                          ? `${(report.salon_summary.machine_total / report.salon_summary.total_revenue) * 100}%`
                          : '0%'
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
              <h3 className="text-lg font-semibold mb-4">إحصائيات سريعة</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                  <span>أعلى إيراد حلاق</span>
                  <span className="font-bold text-primary">
                    {report.barbers_breakdown[0]?.barber_name || '-'} 
                    <span className="text-sm text-muted-foreground ms-2">
                      (<CurrencyDisplay amount={report.barbers_breakdown[0]?.total_revenue || 0} className="text-muted-foreground" useLocale={true} />)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                  <span>أكثر عملاء</span>
                  <span className="font-bold text-blue-500">
                    {report.barbers_breakdown.sort((a, b) => b.total_clients - a.total_clients)[0]?.barber_name || '-'}
                    <span className="text-sm text-muted-foreground ms-2">
                      ({report.barbers_breakdown[0]?.total_clients || 0} عميل)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                  <span>أعلى متوسط عميل</span>
                  <span className="font-bold text-yellow-500">
                    {report.barbers_breakdown.sort((a, b) => b.avg_per_client - a.avg_per_client)[0]?.barber_name || '-'}
                    <span className="text-sm text-muted-foreground ms-2">
                      (<CurrencyDisplay amount={report.barbers_breakdown[0]?.avg_per_client || 0} className="text-muted-foreground" useLocale={true} />)
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
